import * as THREE from 'three';
import { GeoBuilder, propMesh, shadeByHeight } from './geo.js';
import { baseHeightAt, heightAt, slopeAt } from './terrain.js';
import { carveAt, WATER_LEVEL, PLAZA_R, POND, LAKE } from './layout.js';
import { rr, rnd, pick, chance, TAU, smoothstep, fbm2, hash2, lerp, clamp } from './util.js';
import { rowsTexture } from './textures.js';
import { addFence, addHedge, buildScarecrow } from './props.js';

/** Shared wind clock for all swaying materials. */
export const windUniforms = { uTime: { value: 0 } };

export function applyWind(material, { amp = 0.05, freq = 1.4, power = 1.6 } = {}) {
  material.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = windUniforms.uTime;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nuniform float uTime;')
      .replace(
        '#include <begin_vertex>',
        /* glsl */ `
        #include <begin_vertex>
        #ifdef USE_INSTANCING
          vec3 iPos = vec3(instanceMatrix[3][0], 0.0, instanceMatrix[3][2]);
        #else
          vec3 iPos = vec3(0.0);
        #endif
        float wph = uTime * ${freq.toFixed(2)} + iPos.x * 0.21 + iPos.z * 0.17;
        float sw = pow(max(position.y, 0.0), ${power.toFixed(2)}) * ${amp.toFixed(4)};
        transformed.x += (sin(wph) + sin(wph * 2.3 + 1.7) * 0.35) * sw;
        transformed.z += cos(wph * 0.87) * sw * 0.6;
      `,
      );
  };
  material.customProgramCacheKey = () => `wind-${amp}-${freq}-${power}`;
  return material;
}

function windMaterial(opts, matOpts = {}) {
  const m = new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.95, metalness: 0, ...matOpts });
  return applyWind(m, opts);
}

// ------------------------------------------------------------------ geometries
const GREENS = ['#4fae4a', '#5cbc52', '#3f9a44', '#6fc85a'];

function geoBroadleaf(hue = 0) {
  const b = new GeoBuilder();
  b.cyl(0.2, 0.34, 2.9, 6, 0, -0.2, 0, '#8b5a3c', { jitter: 0.1 });
  b.cyl(0.08, 0.16, 1.4, 5, 0.35, 1.6, 0.1, '#8b5a3c', { rz: -0.7 });
  const g = [pick(GREENS), pick(GREENS), pick(GREENS), '#79cc5a'];
  b.ico(2.0, 1, 0, 4.0, 0, g[0], { sy: 0.92, jitter: 0.14 });
  b.ico(1.5, 1, 1.15, 3.35, 0.5, g[1], { jitter: 0.14 });
  b.ico(1.5, 1, -1.0, 3.6, -0.55, g[2], { jitter: 0.14 });
  b.ico(1.25, 1, 0.1, 5.1, 0.1, g[3], { jitter: 0.14 });
  return shadeByHeight(b.build(), 1.9, 6.2, 0.62, 1.12);
}

function geoApple() {
  const b = new GeoBuilder();
  b.cyl(0.18, 0.28, 1.9, 6, 0, -0.2, 0, '#7d5236');
  b.ico(1.75, 1, 0, 2.9, 0, '#5cbc52', { sy: 0.85, jitter: 0.14 });
  b.ico(1.25, 1, 0.9, 2.5, 0.5, '#4fae4a', { jitter: 0.14 });
  b.ico(1.2, 1, -0.9, 2.6, -0.4, '#6fc85a', { jitter: 0.14 });
  for (let i = 0; i < 9; i++) {
    const a = rnd() * TAU, e = rr(-0.2, 0.9), r = 1.7;
    b.ico(0.16, 0, Math.cos(a) * Math.cos(e) * r, 2.9 + Math.sin(e) * r * 0.8, Math.sin(a) * Math.cos(e) * r, '#e63946');
  }
  return shadeByHeight(b.build(), 1.2, 4.4, 0.65, 1.1);
}

function geoPine() {
  const b = new GeoBuilder();
  b.cyl(0.16, 0.28, 1.9, 6, 0, -0.2, 0, '#6b4a33');
  const cols = ['#2f7d4f', '#3a8f57', '#2a7048', '#43a05e'];
  const levels = [[2.0, 2.4, 1.0], [1.65, 2.2, 2.25], [1.28, 2.0, 3.45], [0.9, 1.8, 4.6], [0.5, 1.4, 5.6]];
  levels.forEach(([r, h, y], i) => b.cone(r, h, 7, 0, y, 0, cols[i % 4], { ry: rr(0, 1), jitter: 0.14 }));
  return shadeByHeight(b.build(), 0.8, 7.0, 0.58, 1.15);
}

function geoBirch() {
  const b = new GeoBuilder();
  b.cyl(0.13, 0.2, 4.2, 6, 0, -0.2, 0, '#eeebe0');
  for (let i = 0; i < 6; i++) b.box(0.24, 0.07, 0.24, 0, 0.5 + i * 0.6, 0, '#3a3a3a', { ry: i });
  b.ico(1.25, 1, 0, 4.9, 0, '#9ccf52', { sy: 1.25, jitter: 0.14 });
  b.ico(0.95, 1, 0.55, 4.0, 0.3, '#86c04a', { sy: 1.1, jitter: 0.14 });
  b.ico(0.9, 1, -0.5, 4.3, -0.3, '#a8d65f', { sy: 1.1, jitter: 0.14 });
  return shadeByHeight(b.build(), 2.5, 6.4, 0.7, 1.12);
}

function geoCherry() {
  const b = new GeoBuilder();
  b.cyl(0.22, 0.36, 2.4, 6, 0, -0.2, 0, '#6b4436');
  b.cyl(0.1, 0.18, 1.5, 5, -0.4, 1.9, 0, '#6b4436', { rz: 0.7 });
  const p = ['#f7b3ca', '#ffcfe0', '#f2a0bd', '#ffdde8'];
  b.ico(2.05, 1, 0, 3.7, 0, p[0], { sy: 0.85, jitter: 0.12 });
  b.ico(1.5, 1, 1.3, 3.1, 0.3, p[1], { jitter: 0.12 });
  b.ico(1.55, 1, -1.25, 3.3, -0.4, p[2], { jitter: 0.12 });
  b.ico(1.2, 1, 0.1, 4.75, 0.2, p[3], { jitter: 0.12 });
  return shadeByHeight(b.build(), 1.7, 5.9, 0.75, 1.12);
}

function geoAutumn() {
  const b = new GeoBuilder();
  b.cyl(0.2, 0.32, 2.7, 6, 0, -0.2, 0, '#7d5236');
  const p = ['#f0902f', '#e2622c', '#f5b83a', '#d94c2a'];
  b.ico(1.95, 1, 0, 3.9, 0, p[0], { sy: 0.92, jitter: 0.14 });
  b.ico(1.45, 1, 1.1, 3.3, 0.5, p[1], { jitter: 0.14 });
  b.ico(1.45, 1, -1.0, 3.5, -0.5, p[2], { jitter: 0.14 });
  b.ico(1.2, 1, 0.1, 4.95, 0.1, p[3], { jitter: 0.14 });
  return shadeByHeight(b.build(), 1.8, 6.1, 0.66, 1.12);
}

function geoBush() {
  const b = new GeoBuilder();
  b.ico(0.75, 1, 0, 0.55, 0, '#4fae4a', { sy: 0.8, jitter: 0.16 });
  b.ico(0.55, 1, 0.6, 0.4, 0.2, '#5cbc52', { sy: 0.8, jitter: 0.16 });
  b.ico(0.5, 1, -0.5, 0.42, -0.3, '#3f9a44', { sy: 0.8, jitter: 0.16 });
  return shadeByHeight(b.build(), 0, 1.1, 0.65, 1.15);
}

function geoFlowerBush() {
  const b = new GeoBuilder();
  b.ico(0.7, 1, 0, 0.5, 0, '#4fae4a', { sy: 0.75, jitter: 0.16 });
  for (let i = 0; i < 8; i++) {
    const a = rnd() * TAU, e = rr(0.1, 1.0);
    b.ico(0.13, 0, Math.cos(a) * Math.cos(e) * 0.75, 0.5 + Math.sin(e) * 0.55, Math.sin(a) * Math.cos(e) * 0.75, '#ffffff');
  }
  return b.build();
}

function geoRock() {
  const b = new GeoBuilder();
  b.ico(0.8, 0, 0, 0.3, 0, '#9d9891', { sy: 0.7, jitter: 0.22 });
  b.ico(0.5, 0, 0.6, 0.18, 0.2, '#8d8882', { sy: 0.75, jitter: 0.22 });
  return shadeByHeight(b.build(), 0, 1, 0.75, 1.08);
}

function geoTuft() {
  const b = new GeoBuilder();
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * TAU + rr(-0.4, 0.4);
    const h = rr(0.42, 0.7), t = 0.3;
    b.cone(0.075, h, 3, Math.cos(a) * 0.07, 0, Math.sin(a) * 0.07, '#ffffff', { rx: Math.sin(a) * t, rz: -Math.cos(a) * t });
  }
  return shadeByHeight(b.build(), 0, 0.7, 0.55, 1.15);
}

function geoFlowerHead() {
  const b = new GeoBuilder();
  b.ico(0.11, 0, 0, 0.36, 0, '#ffffff', { sy: 0.7 });
  b.box(0.03, 0.34, 0.03, 0, 0, 0, '#3f8f3a');
  return b.build();
}

function geoReed() {
  const b = new GeoBuilder();
  for (let i = 0; i < 5; i++) {
    const a = rnd() * TAU, r = rnd() * 0.25, h = rr(1.4, 2.2);
    b.cone(0.06, h, 3, Math.cos(a) * r, 0, Math.sin(a) * r, '#6fa04a', { rx: rr(-0.12, 0.12), rz: rr(-0.12, 0.12) });
    if (i % 2 === 0) b.cyl(0.07, 0.07, 0.35, 5, Math.cos(a) * r, h * 0.62, Math.sin(a) * r, '#7a4a2a');
  }
  return shadeByHeight(b.build(), 0, 2.2, 0.6, 1.1);
}

function geoLily() {
  const b = new GeoBuilder();
  b.cyl(0.55, 0.55, 0.04, 9, 0, 0, 0, '#4caf50', { jitter: 0.1 });
  b.cyl(0.1, 0.1, 0.06, 5, 0.1, 0.03, 0.05, '#f7a1c4');
  return b.build();
}

// crops --------------------------------------------------------------
function geoWheat() {
  const b = new GeoBuilder();
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * TAU + rr(-0.3, 0.3), r = rr(0.05, 0.3), h = rr(0.85, 1.15);
    const t = 0.16;
    const x = Math.cos(a) * r, z = Math.sin(a) * r;
    b.cone(0.05, h, 3, x, 0, z, '#ffffff', { rx: Math.sin(a) * t, rz: -Math.cos(a) * t });
    b.ico(0.075, 0, x + Math.cos(a) * h * t, h - 0.02, z + Math.sin(a) * h * t, '#ffffff', { sy: 2.0 });
  }
  return shadeByHeight(b.build(), 0, 1.15, 0.7, 1.15);
}

function geoCorn() {
  const b = new GeoBuilder();
  b.cyl(0.05, 0.08, 2.2, 5, 0, 0, 0, '#ffffff');
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * TAU + rr(-0.3, 0.3), y = 0.5 + i * 0.36;
    b.boxC(0.62, 0.025, 0.14, Math.cos(a) * 0.3, y, Math.sin(a) * 0.3, '#ffffff', { ry: -a, rz: -0.35 });
  }
  b.cone(0.08, 0.35, 4, 0, 2.2, 0, '#ffffff');
  b.ico(0.1, 0, 0.1, 1.3, 0, '#ffffff', { sy: 1.8 });
  return shadeByHeight(b.build(), 0, 2.5, 0.7, 1.15);
}

function geoSunflower() {
  const b = new GeoBuilder();
  b.cyl(0.05, 0.07, 1.65, 5, 0, 0, 0, '#5a9a3c');
  for (let i = 0; i < 2; i++) b.boxC(0.4, 0.02, 0.18, (i ? 1 : -1) * 0.2, 0.7 + i * 0.3, 0, '#4f8f3a', { rz: (i ? 1 : -1) * 0.5 });
  const hy = 1.72, tilt = -0.65;
  b.cyl(0.36, 0.36, 0.1, 10, 0, hy, 0, '#6b3f1e', { rx: Math.PI / 2 + tilt });
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * TAU;
    const px = Math.cos(a) * 0.42, py = Math.sin(a) * 0.42;
    b.cone(0.11, 0.3, 3, px, hy + py * Math.cos(tilt), py * Math.sin(tilt), '#ffd23a', { rz: a - Math.PI / 2, rx: tilt });
  }
  return shadeByHeight(b.build(), 0, 2.2, 0.75, 1.1);
}

function geoCabbage() {
  const b = new GeoBuilder();
  b.ico(0.42, 1, 0, 0.26, 0, '#5da84a', { sy: 0.72, jitter: 0.14 });
  b.ico(0.27, 1, 0, 0.36, 0, '#9ad35a', { sy: 0.78, jitter: 0.14 });
  return b.build();
}

function geoLavender() {
  const b = new GeoBuilder();
  b.ico(0.4, 0, 0, 0.18, 0, '#6e8f5a', { sy: 0.5, jitter: 0.1 });
  for (let i = 0; i < 7; i++) {
    const a = (i / 7) * TAU, r = rr(0.05, 0.28), h = rr(0.5, 0.75);
    b.cone(0.055, h, 4, Math.cos(a) * r, 0.1, Math.sin(a) * r, '#9a74e6', { jitter: 0.12, rx: Math.sin(a) * 0.15, rz: -Math.cos(a) * 0.15 });
  }
  return shadeByHeight(b.build(), 0, 0.9, 0.7, 1.12);
}

function geoPumpkin() {
  const b = new GeoBuilder();
  b.ico(0.46, 1, 0, 0.32, 0, '#f08a24', { sy: 0.78, jitter: 0.12 });
  b.box(0.08, 0.16, 0.08, 0, 0.6, 0, '#5a7a2a');
  b.boxC(0.6, 0.03, 0.34, 0.5, 0.06, 0.25, '#4f9e3c', { ry: 0.6 });
  return b.build();
}

function geoCanola() {
  const b = new GeoBuilder();
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * TAU + rr(-0.3, 0.3), r = rr(0.05, 0.3), h = rr(0.75, 1.05);
    const x = Math.cos(a) * r, z = Math.sin(a) * r;
    b.cone(0.03, h, 3, x, 0, z, '#5a9a3c');
    b.ico(0.16, 0, x, h, z, '#ffe14a', { sy: 0.7, jitter: 0.1 });
  }
  return shadeByHeight(b.build(), 0, 1.1, 0.75, 1.1);
}

function geoSprout() {
  const b = new GeoBuilder();
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * TAU;
    b.cone(0.05, 0.25, 3, Math.cos(a) * 0.05, 0, Math.sin(a) * 0.05, '#7fd05a', { rx: Math.sin(a) * 0.5, rz: -Math.cos(a) * 0.5 });
  }
  return b.build();
}

// ------------------------------------------------------------------ instancing helpers
const _m = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _e = new THREE.Euler();
const _p = new THREE.Vector3();
const _s = new THREE.Vector3();
const _c = new THREE.Color();

/** Build chunked InstancedMeshes (one per spatial cell) so frustum + shadow culling still work. */
function buildChunks(group, geometry, material, items, { cell = 64, cast = true, receive = true, name = '' } = {}) {
  const cells = new Map();
  for (const it of items) {
    const key = `${Math.floor(it.x / cell)},${Math.floor(it.z / cell)}`;
    let arr = cells.get(key);
    if (!arr) cells.set(key, (arr = []));
    arr.push(it);
  }
  for (const [, arr] of cells) {
    const im = new THREE.InstancedMesh(geometry, material, arr.length);
    arr.forEach((it, i) => {
      _e.set(it.tx || 0, it.ry || 0, it.tz || 0, 'YXZ');
      _q.setFromEuler(_e);
      _p.set(it.x, it.y, it.z);
      _s.set(it.sx ?? it.s ?? 1, it.sy ?? it.s ?? 1, it.sz ?? it.s ?? 1);
      _m.compose(_p, _q, _s);
      im.setMatrixAt(i, _m);
      if (it.color) im.setColorAt(i, _c.set(it.color));
    });
    im.instanceMatrix.needsUpdate = true;
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
    im.castShadow = cast;
    im.receiveShadow = receive;
    im.computeBoundingSphere();
    im.name = name;
    group.add(im);
  }
}

const tint = (h, s, l) => new THREE.Color().setHSL(h, s, l);

// ------------------------------------------------------------------ main
export function createVegetation(occ, village) {
  const group = new THREE.Group();
  group.name = 'vegetation';
  const info = { pens: [], fields: [] };

  const R = { x: 0 };
  const groundY = (x, z) => heightAt(x, z);
  const toW = (cx, cz, ang, lx, lz) => [cx + lx * Math.cos(ang) + lz * Math.sin(ang), cz - lx * Math.sin(ang) + lz * Math.cos(ang)];

  // ============================================================ fields
  const FIELD_KINDS = [
    { key: 'wheat', ground: '#c9a24a', w: 4 },
    { key: 'wheat2', ground: '#d4b25a', w: 2 },
    { key: 'corn', ground: '#5f8f38', w: 2 },
    { key: 'sunflower', ground: '#7aa040', w: 2 },
    { key: 'cabbage', ground: '#6e4c32', w: 2 },
    { key: 'lavender', ground: '#6a8e45', w: 1 },
    { key: 'pumpkin', ground: '#79a83e', w: 1 },
    { key: 'canola', ground: '#a4c23c', w: 2 },
    { key: 'tilled', ground: '#7c5438', w: 2 },
    { key: 'pasture', ground: '#86d45c', w: 3 },
    { key: 'orchard', ground: '#82cf58', w: 1 },
  ];
  const pickKind = () => {
    const total = FIELD_KINDS.reduce((a, k) => a + k.w, 0);
    let r = rnd() * total;
    for (const k of FIELD_KINDS) { r -= k.w; if (r <= 0) return k; }
    return FIELD_KINDS[0];
  };

  const rowTex = rowsTexture();
  const plantItems = { wheat: [], corn: [], sunflower: [], cabbage: [], lavender: [], pumpkin: [], canola: [], sprout: [], apple: [] };
  const hedgeB = new GeoBuilder();
  const fenceB = new GeoBuilder();
  const fieldMeshes = [];
  const scarecrowKit = [];

  function fieldOK(cx, cz, w, d, ang) {
    for (const [sx, sz] of [[0, 0], [-1, -1], [1, -1], [-1, 1], [1, 1], [0, -1], [0, 1], [-1, 0], [1, 0]]) {
      const [x, z] = toW(cx, cz, ang, (sx * w) / 2, (sz * d) / 2);
      if (carveAt(x, z) > 0.0005) return false;
      if (slopeAt(x, z, 1.5) > 0.11) return false;
      const r = Math.hypot(x, z);
      if (r > 86 || r < 30) return false;
    }
    return occ.rectFree(cx, cz, w, d, ang, 1.0);
  }

  const fields = [];
  {
    // several passes: big fields first, then fill gaps with smaller ones
    const sizes = [[24, 20, 40], [20, 16, 60], [15, 12, 80]];
    for (const [W, D, tries] of sizes) {
      for (let a = 0; a < 900 && fields.length < 24; a++) {
        const r = rr(34, 84), th = rnd() * TAU;
        const cx = Math.cos(th) * r, cz = Math.sin(th) * r;
        const sector = Math.floor(((th + TAU) % TAU) / (TAU / 5));
        const ang = sector * 0.63 + 0.25 + rr(-0.08, 0.08);
        const w = W + rr(-3, 3), d = D + rr(-3, 3);
        if (!fieldOK(cx, cz, w, d, ang)) continue;
        occ.rect(cx, cz, w, d, ang, 0.9);
        fields.push({ cx, cz, w, d, ang, kind: pickKind() });
        if (fields.filter((f) => f.w > W - 4).length > tries) break;
      }
    }
  }
  // guarantee some variety: make sure a few specific kinds exist
  const forced = ['pasture', 'orchard', 'sunflower', 'wheat', 'canola'];
  forced.forEach((k, i) => { if (fields[i]) fields[i].kind = FIELD_KINDS.find((x) => x.key === k); });

  for (const f of fields) {
    const { cx, cz, w, d, ang, kind } = f;
    // ---- ground patch (follows terrain)
    const nx = Math.max(2, Math.ceil(w / 1.6)), nz = Math.max(2, Math.ceil(d / 1.6));
    const g = new THREE.PlaneGeometry(w, d, nx, nz).rotateX(-Math.PI / 2);
    const pos = g.attributes.position;
    const rowStep = kind.key === 'pasture' || kind.key === 'orchard' ? 0 : 1.4;
    for (let i = 0; i < pos.count; i++) {
      const [x, z] = toW(cx, cz, ang, pos.getX(i), pos.getZ(i));
      pos.setY(i, baseHeightAt(x, z) + 0.055);
    }
    const uv = g.attributes.uv;
    for (let i = 0; i < uv.count; i++) uv.setXY(i, (uv.getX(i) * w) / 1.4, (uv.getY(i) * d) / 2.0);
    g.computeVertexNormals();
    const isRows = rowStep > 0;
    const mat = new THREE.MeshStandardMaterial({
      color: kind.ground,
      map: isRows ? rowTex : null,
      roughness: 1,
      flatShading: false,
      polygonOffset: true,
      polygonOffsetFactor: -1.5,
      polygonOffsetUnits: -1.5,
    });
    const mesh = new THREE.Mesh(g, mat);
    mesh.position.set(cx, 0, cz);
    // geometry vertices are local; rotate around Y about the field centre but heights are world -> keep Y separate
    mesh.rotation.y = ang;
    mesh.receiveShadow = true;
    // heights were sampled in world space but the mesh is transformed; positions y unaffected by Y-rotation ✓
    group.add(mesh);
    fieldMeshes.push(mesh);

    // ---- plants
    const put = (list, sxs, szs, opt = {}) => {
      const cols = Math.floor((w - 0.8) / sxs), rows = Math.floor((d - 1.2) / szs);
      for (let i = 0; i <= cols; i++) {
        for (let j = 0; j <= rows; j++) {
          const lx = -w / 2 + 0.35 + i * sxs + rr(-0.06, 0.06);
          const lz = -d / 2 + 0.6 + j * szs + rr(-0.12, 0.12);
          const [x, z] = toW(cx, cz, ang, lx, lz);
          list.push({
            x, z, y: baseHeightAt(x, z) + 0.04, ry: rnd() * TAU,
            s: (opt.s ?? 1) * rr(0.85, 1.2), color: opt.color ? opt.color() : null,
          });
        }
      }
    };
    switch (kind.key) {
      case 'wheat': put(plantItems.wheat, 1.4, 0.6, { color: () => tint(rr(0.11, 0.135), 0.6, rr(0.55, 0.68)) }); break;
      case 'wheat2': put(plantItems.wheat, 1.4, 0.6, { color: () => tint(rr(0.14, 0.17), 0.55, rr(0.5, 0.6)) }); break;
      case 'corn': put(plantItems.corn, 1.4, 0.95, { color: () => tint(rr(0.2, 0.26), 0.5, rr(0.5, 0.6)) }); break;
      case 'sunflower': put(plantItems.sunflower, 1.4, 1.2, { color: () => tint(rr(0.11, 0.15), 0.5, rr(0.65, 0.8)) }); break;
      case 'cabbage': put(plantItems.cabbage, 1.4, 1.0, { color: () => tint(rr(0.22, 0.32), 0.45, rr(0.6, 0.78)) }); break;
      case 'lavender': put(plantItems.lavender, 1.4, 0.85, { color: () => tint(rr(0.72, 0.78), 0.55, rr(0.7, 0.85)) }); break;
      case 'pumpkin': put(plantItems.pumpkin, 2.8, 2.4, { color: () => tint(rr(0.06, 0.09), 0.85, rr(0.6, 0.72)) }); break;
      case 'canola': put(plantItems.canola, 1.4, 0.7, { color: () => tint(rr(0.14, 0.17), 0.85, rr(0.7, 0.85)) }); break;
      case 'tilled': put(plantItems.sprout, 1.4, 0.9, { color: () => tint(rr(0.25, 0.3), 0.6, rr(0.6, 0.75)) }); break;
      case 'orchard': {
        for (let i = 0; (i + 0.5) * 6.0 < w - 3; i++) {
          for (let j = 0; (j + 0.5) * 5.4 < d - 2; j++) {
            const [x, z] = toW(cx, cz, ang, -w / 2 + 3.4 + i * 6.0, -d / 2 + 3.0 + j * 5.4);
            plantItems.apple.push({ x, z, y: baseHeightAt(x, z) - 0.05, ry: rnd() * TAU, s: rr(0.85, 1.0), color: tint(rr(0.22, 0.3), 0.3, rr(0.9, 1.0)) });
          }
        }
        break;
      }
      case 'pasture': {
        const corners = [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([sx, sz]) => {
          const [x, z] = toW(cx, cz, ang, (sx * (w + 0.4)) / 2, (sz * (d + 0.4)) / 2);
          return { x, z, y: baseHeightAt(x, z) };
        });
        addFence(fenceB, corners, { closed: true, color: '#e9dcc0' });
        info.pens.push({ x: cx, z: cz, w: w - 1.5, d: d - 1.5, ry: ang, kind: chance(0.65) ? 'sheep' : 'cow' });
        break;
      }
    }

    // ---- borders: hedges / fences on some sides (with gaps)
    if (kind.key !== 'pasture') {
      const sides = [
        [[-1, -1], [1, -1]], [[1, -1], [1, 1]], [[1, 1], [-1, 1]], [[-1, 1], [-1, -1]],
      ];
      for (const [[ax, az], [bx, bz]] of sides) {
        if (chance(0.4)) continue;
        const mg = 0.55;
        const pts = [];
        const len = Math.hypot((bx - ax) * w, (bz - az) * d);
        const n = Math.max(2, Math.round(len / 3));
        // random gap
        const gapAt = chance(0.5) ? rr(0.25, 0.75) : -1;
        for (let i = 0; i <= n; i++) {
          const t = i / n;
          if (gapAt > 0 && Math.abs(t - gapAt) < 0.08) { if (pts.length > 1) { addHedge(hedgeB, pts.splice(0)); } continue; }
          const [x, z] = toW(cx, cz, ang, lerp(ax, bx, t) * (w / 2 + mg), lerp(az, bz, t) * (d / 2 + mg));
          pts.push({ x, z, y: baseHeightAt(x, z) });
        }
        if (pts.length > 1) addHedge(hedgeB, pts);
      }
    }
    info.fields.push(f);
    // scarecrow
    if ((kind.key === 'wheat' || kind.key === 'corn' || kind.key === 'sunflower') && chance(0.7)) {
      const [x, z] = toW(cx, cz, ang, rr(-w * 0.2, w * 0.2), rr(-d * 0.2, d * 0.2));
      scarecrowKit.push({ x, z, y: baseHeightAt(x, z), ry: rnd() * TAU });
    }
  }

  // ---- crop instanced meshes
  const cropDefs = [
    ['wheat', geoWheat(), { amp: 0.22, freq: 1.6, power: 1.4 }],
    ['corn', geoCorn(), { amp: 0.06, freq: 1.2, power: 1.6 }],
    ['sunflower', geoSunflower(), { amp: 0.07, freq: 1.1, power: 1.6 }],
    ['cabbage', geoCabbage(), { amp: 0.0, freq: 1, power: 1 }],
    ['lavender', geoLavender(), { amp: 0.25, freq: 1.8, power: 1.5 }],
    ['pumpkin', geoPumpkin(), { amp: 0, freq: 1, power: 1 }],
    ['canola', geoCanola(), { amp: 0.22, freq: 1.7, power: 1.4 }],
    ['sprout', geoSprout(), { amp: 0.3, freq: 1.5, power: 1.3 }],
  ];
  for (const [key, geo, wind] of cropDefs) {
    if (!plantItems[key].length) continue;
    const mat = wind.amp > 0 ? windMaterial(wind) : new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.95 });
    buildChunks(group, geo, mat, plantItems[key], { cell: 48, cast: key === 'corn' || key === 'sunflower', name: `crop-${key}` });
  }
  if (plantItems.apple.length) buildChunks(group, geoApple(), windMaterial({ amp: 0.02, freq: 1.1, power: 1.5 }), plantItems.apple, { cell: 64, name: 'apple' });
  const hb = hedgeB.build();
  if (hb) {
    const hm = new THREE.Mesh(hb, windMaterial({ amp: 0.03, freq: 1.3, power: 1.6 }));
    hm.castShadow = true;
    hm.receiveShadow = true;
    group.add(hm);
  }
  const fb = fenceB.build();
  if (fb) group.add(propMesh(fb));
  if (scarecrowKit.length) {
    const b = new GeoBuilder();
    for (const s of scarecrowKit) {
      const K = buildScarecrow();
      for (const part of K.body.parts) {
        const g = part.clone();
        g.applyMatrix4(new THREE.Matrix4().compose(new THREE.Vector3(s.x, s.y, s.z), new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), s.ry), new THREE.Vector3(1, 1, 1)));
        b.parts.push(g);
      }
    }
    const sm = b.build();
    if (sm) group.add(propMesh(sm));
  }

  // ============================================================ trees
  const treeItems = { broad: [], pine: [], birch: [], cherry: [], autumn: [], bush: [], flowerBush: [], rock: [] };
  const treeTint = () => tint(rr(0.0, 1.0) > 2 ? 0 : 0.3, 0, rr(0.86, 1.08)); // brightness only
  const jitterColor = (rangeH = 0.03) => new THREE.Color().setRGB(rr(0.9, 1.08), rr(0.92, 1.08), rr(0.88, 1.06));
  const addTree = (kind, x, z, s = 1) => {
    treeItems[kind].push({
      x, z, y: groundY(x, z) - 0.05, ry: rnd() * TAU, s: s * rr(0.85, 1.25), sy: s * rr(0.9, 1.3), tx: rr(-0.03, 0.03), tz: rr(-0.03, 0.03), color: jitterColor(),
    });
  };

  // plaza cherry trees
  for (const t of village.info.plazaTrees) {
    addTree('cherry', t.x, t.z, 1.0);
    occ.disc(t.x, t.z, 2.0, 1);
  }
  // scattered
  {
    const attempts = 42000;
    for (let i = 0; i < attempts; i++) {
      const r = Math.sqrt(rnd()) * 190;
      const th = rnd() * TAU;
      const x = Math.cos(th) * r, z = Math.sin(th) * r;
      const h = baseHeightAt(x, z);
      if (h > 34) continue;
      const hills = smoothstep(66, 96, r);
      // density: sparse groves in the valley, dense forest on the hills
      const grove = fbm2(x * 0.035 + 20, z * 0.035 - 14, 3) * 0.5 + 0.5;
      let dens = lerp(smoothstep(0.52, 0.72, grove) * 0.75 + 0.03, 0.95, hills);
      if (r < 30) dens = 0.0;
      if (h > 22) dens *= 0.4;
      if (rnd() > dens) continue;
      if (!occ.isFree(x, z, 1.6)) continue;
      if (carveAt(x, z) > 0.0005) continue;
      if (slopeAt(x, z, 1.0) > 0.75) continue;
      const roll = rnd();
      if (hills > 0.5) {
        if (roll < 0.78) addTree('pine', x, z, rr(1.0, 1.6));
        else if (roll < 0.9) addTree('birch', x, z, 1.1);
        else if (roll < 0.96) addTree('autumn', x, z, 1.1);
        else addTree('broad', x, z, 1.2);
      } else {
        if (roll < 0.5) addTree('broad', x, z, rr(0.9, 1.25));
        else if (roll < 0.66) addTree('pine', x, z, 1.0);
        else if (roll < 0.8) addTree('birch', x, z, 1.0);
        else if (roll < 0.9) addTree('autumn', x, z, 1.05);
        else if (chance(0.45)) addTree('cherry', x, z, 0.85);
        else addTree('broad', x, z, 1.0);
      }
      occ.disc(x, z, 1.35, 1);
    }
  }
  // bushes near tree lines / roads, flower bushes near the village
  for (let i = 0; i < 2600; i++) {
    const r = Math.sqrt(rnd()) * 120, th = rnd() * TAU;
    const x = Math.cos(th) * r, z = Math.sin(th) * r;
    if (!occ.isFree(x, z, 0.8) || carveAt(x, z) > 0.0005) continue;
    if (baseHeightAt(x, z) > 24 || slopeAt(x, z, 1.0) > 0.45) continue;
    const near = r < 34;
    const kind = near && chance(0.6) ? 'flowerBush' : 'bush';
    treeItems[kind].push({ x, z, y: groundY(x, z) - 0.05, ry: rnd() * TAU, s: rr(0.7, 1.3), tx: 0, tz: 0, color: jitterColor() });
    if (chance(0.4)) occ.disc(x, z, 0.6, 1);
  }
  // rocks (shores + hills)
  for (let i = 0; i < 1600; i++) {
    const r = Math.sqrt(rnd()) * 150, th = rnd() * TAU;
    const x = Math.cos(th) * r, z = Math.sin(th) * r;
    const c = carveAt(x, z);
    const shore = c > 0.25 && c < 0.6;
    const hill = r > 70 && chance(0.15);
    if (!shore && !hill) continue;
    if (!shore && !occ.isFree(x, z, 1.2)) continue;
    const h = heightAt(x, z);
    if (shore && (h < -0.25 || h > 0.4)) continue;
    treeItems.rock.push({ x, z, y: h - 0.1, ry: rnd() * TAU, s: shore ? rr(0.5, 1.0) : rr(0.8, 2.6), sy: rr(0.7, 1.2), tx: rr(-0.2, 0.2), tz: rr(-0.2, 0.2), color: new THREE.Color().setRGB(1, 1, 1).multiplyScalar(rr(0.85, 1.1)) });
  }

  const treeDefs = [
    ['broad', geoBroadleaf(), { amp: 0.045, freq: 1.0, power: 1.7 }],
    ['pine', geoPine(), { amp: 0.03, freq: 1.2, power: 1.8 }],
    ['birch', geoBirch(), { amp: 0.05, freq: 1.3, power: 1.7 }],
    ['cherry', geoCherry(), { amp: 0.045, freq: 1.0, power: 1.7 }],
    ['autumn', geoAutumn(), { amp: 0.045, freq: 1.1, power: 1.7 }],
    ['bush', geoBush(), { amp: 0.04, freq: 1.5, power: 1.4 }],
    ['flowerBush', geoFlowerBush(), { amp: 0.03, freq: 1.5, power: 1.4 }],
  ];
  for (const [key, geo, wind] of treeDefs) {
    if (!treeItems[key].length) continue;
    buildChunks(group, geo, windMaterial(wind), treeItems[key], { cell: 64, cast: key !== 'bush' && key !== 'flowerBush', name: `tree-${key}` });
  }
  if (treeItems.rock.length) {
    buildChunks(group, geoRock(), new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 1 }), treeItems.rock, { cell: 96, name: 'rocks' });
  }

  // ============================================================ ground cover
  {
    const tufts = [], heads = [];
    const tuftGreens = ['#69bf45', '#7fd050', '#5aae40', '#9ad85a', '#b8d95c'];
    let tries = 0;
    while (tufts.length < 26000 && tries++ < 120000) {
      const r = Math.sqrt(rnd()) * 105, th = rnd() * TAU;
      const x = Math.cos(th) * r, z = Math.sin(th) * r;
      if (!occ.isFree(x, z, 0.3)) continue;
      const c = carveAt(x, z);
      if (c > 0.3) continue;
      const h = heightAt(x, z);
      if (h < 0.42) continue;
      if (h > 26) continue;
      const density = 0.55 + 0.45 * (fbm2(x * 0.05, z * 0.05, 2) * 0.5 + 0.5);
      if (rnd() > density) continue;
      tufts.push({ x, z, y: h - 0.03, ry: rnd() * TAU, s: rr(0.7, 1.5), color: new THREE.Color(pick(tuftGreens)).multiplyScalar(rr(0.9, 1.15)) });
      if (chance(0.075) && r < 95) {
        const petals = ['#ffffff', '#ffe066', '#ff8fab', '#c9a0ff', '#ff9a5a', '#8ecbff'];
        heads.push({ x: x + rr(-0.2, 0.2), z: z + rr(-0.2, 0.2), y: h - 0.02, ry: rnd() * TAU, s: rr(0.8, 1.4), color: new THREE.Color(pick(petals)) });
      }
    }
    buildChunks(group, geoTuft(), windMaterial({ amp: 0.35, freq: 1.7, power: 1.5 }), tufts, { cell: 48, cast: false, name: 'tufts' });
    buildChunks(group, geoFlowerHead(), windMaterial({ amp: 0.12, freq: 1.8, power: 1.5 }), heads, { cell: 48, cast: false, name: 'flowers' });
  }

  // ============================================================ reeds + lilies on water margins
  {
    const reeds = [], lilies = [];
    for (let i = 0; i < 9000 && reeds.length < 420; i++) {
      const L = chance(0.5) ? LAKE : POND;
      const a = rnd() * TAU, rr2 = L.r * rr(0.7, 1.5);
      const x = L.x + Math.cos(a) * rr2, z = L.z + Math.sin(a) * rr2;
      const h = heightAt(x, z);
      if (h > -0.35 && h < 0.35 && chance(0.7)) reeds.push({ x, z, y: h - 0.1, ry: rnd() * TAU, s: rr(0.7, 1.3), color: new THREE.Color(1, 1, 1).multiplyScalar(rr(0.85, 1.1)) });
    }
    // reeds along the river banks too
    for (let i = 0; i < 6000 && reeds.length < 620; i++) {
      const cx = (LAKE.x + POND.x) / 2 + rr(-30, 30), cz = (LAKE.z + POND.z) / 2 + rr(-30, 30);
      const h = heightAt(cx, cz), c = carveAt(cx, cz);
      if (c > 0.3 && c < 0.5 && h > -0.3 && h < 0.3 && chance(0.4)) reeds.push({ x: cx, z: cz, y: h - 0.1, ry: rnd() * TAU, s: rr(0.6, 1.2), color: new THREE.Color(1, 1, 1).multiplyScalar(rr(0.85, 1.1)) });
    }
    for (let i = 0; i < 60; i++) {
      const L = chance(0.55) ? LAKE : POND;
      const a = rnd() * TAU, r = L.r * Math.sqrt(rnd()) * 0.75;
      const x = L.x + Math.cos(a) * r, z = L.z + Math.sin(a) * r;
      if (heightAt(x, z) < -0.5) lilies.push({ x, z, y: WATER_LEVEL + 0.04, ry: rnd() * TAU, s: rr(0.7, 1.3) });
    }
    buildChunks(group, geoReed(), windMaterial({ amp: 0.12, freq: 1.4, power: 1.4 }), reeds, { cell: 96, cast: false, name: 'reeds' });
    buildChunks(group, geoLily(), new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true, roughness: 0.8 }), lilies, { cell: 96, cast: false, name: 'lilies' });
  }

  // ============================================================ flower beds at the plaza
  {
    const petals = ['#ff6f91', '#ffd166', '#ffffff', '#c77dff', '#ff9a5a'];
    const heads = [];
    for (const bed of village.info.flowerBeds) {
      for (let i = 0; i < 26; i++) {
        const a = rnd() * TAU, r = Math.sqrt(rnd()) * bed.r;
        const x = bed.x + Math.cos(a) * r, z = bed.z + Math.sin(a) * r;
        heads.push({ x, z, y: baseHeightAt(x, z) + 0.1, ry: 0, s: rr(1.2, 1.9), color: new THREE.Color(pick(petals)) });
      }
    }
    if (heads.length) buildChunks(group, geoFlowerHead(), windMaterial({ amp: 0.1, freq: 1.8, power: 1.5 }), heads, { cell: 200, cast: false, name: 'plaza-flowers' });
  }

  info.treeItems = treeItems;
  info.tall = [];
  const tallSpec = { broad: [1.9, 6.2], pine: [1.3, 7.2], birch: [1.0, 6.4], cherry: [1.9, 5.9], autumn: [1.9, 6.1], apple: [1.6, 4.4] };
  for (const [k, [r, h]] of Object.entries(tallSpec)) for (const it of treeItems[k] || []) info.tall.push({ x: it.x, z: it.z, r: r * (it.s || 1) * 0.8, h: h * (it.sy || it.s || 1) });
  for (const it of plantItems.apple) info.tall.push({ x: it.x, z: it.z, r: 1.5, h: 4.2 * (it.s || 1) });
  for (const it of plantItems.corn) info.tall.push({ x: it.x, z: it.z, r: 0.6, h: 2.0 });
  return { group, info };
}
